<template>
  <b-modal
    id="users-modal"
    :title="$t('admcfg.userRecovery', 'User Password Recovery')"
    size="md"
    @show="resetForm"
  >
    <div class="users-modal-content">
      <!-- Email Input -->
      <b-form-group
        :label="$t('general.email', 'Email')"
        label-for="recovery-email"
        class="mb-4"
      >
        <b-form-input
          id="recovery-email"
          v-model="userEmail"
          type="email"
          placeholder="user@example.com"
          :state="emailState"
          @keyup.enter="searchUser"
        />
        <b-form-invalid-feedback v-if="emailState === false">
          Please enter a valid email address
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner small />
        <p class="mt-2 text-muted">
          Searching...
        </p>
      </div>

      <!-- Error State -->
      <b-alert v-if="errorMessage" show variant="danger" class="mb-3">
        <i class="fas fa-exclamation-circle mr-2" />
        {{ errorMessage }}
      </b-alert>

      <!-- Success State - User Found -->
      <div v-if="userFound && !isLoading" class="user-found-section">
        <b-alert show variant="success" class="mb-3">
          <i class="fas fa-check-circle mr-2" />
          User found!
        </b-alert>

        <div class="password-display">
          <div class="password-label">
          Password:
        </div>
          <div class="password-content">
            <code class="password-text">{{ decodedPassword }}</code>
            <b-button
              size="sm"
              variant="outline-primary"
              @click="copyToClipboard"
              class="ml-2"
              title="Copy to clipboard"
            >
              <i class="fas fa-copy" />
            </b-button>
          </div>
          <div v-if="copySuccess" class="copy-success">
            ✓ Copied to clipboard
          </div>
        </div>
      </div>

      <!-- No User State -->
      <div v-if="!userFound && userSearched && !isLoading" class="text-center py-3">
        <i class="fas fa-user-slash text-muted mb-3" style="font-size: 2rem; display: block;" />
        <p class="text-muted">
          {{ userNotFoundMessage }}
        </p>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button variant="secondary" @click="cancel">
        {{ $t('general.close', 'Close') }}
      </b-button>
      <b-button
        variant="primary"
        @click="searchUser"
        :disabled="!isEmailValid || isLoading"
      >
        <i class="fas fa-search mr-2" />
        {{ $t('general.search', 'Search') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { elBackAxios } from '@/services/myAxios'

export default {
  name: 'UsersModal',
  data() {
    return {
      userEmail: '',
      isLoading: false,
      errorMessage: '',
      userFound: false,
      userSearched: false,
      decodedPassword: '',
      userNotFoundMessage: '',
      copySuccess: false,
    }
  },
  computed: {
    isEmailValid() {
      // Simple email validation
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.userEmail)
    },
    emailState() {
      if (!this.userEmail) return null
      return this.isEmailValid ? true : false
    },
  },
  methods: {
    resetForm() {
      this.userEmail = ''
      this.isLoading = false
      this.errorMessage = ''
      this.userFound = false
      this.userSearched = false
      this.decodedPassword = ''
      this.copySuccess = false
    },

    async searchUser() {
      if (!this.isEmailValid) {
        this.errorMessage = 'Please enter a valid email address'
        return
      }

      this.isLoading = true
      this.errorMessage = ''
      this.userFound = false
      this.userSearched = false
      this.copySuccess = false

      try {
        const response = await elBackAxios.get('api/services/admin/user-password', {
          params: { email: this.userEmail }
        })

        this.userSearched = true

        if (response.data.found) {
          this.decodedPassword = this.decodePassword(response.data.password_encoded)
          this.userFound = true
        } else {
          this.userNotFoundMessage = response.data.message || 'User not found'
          this.userFound = false
        }
      } catch (error) {
        this.userSearched = true
        const errorMsg = error.response?.data?.message || error.message || 'Failed to search user'
        this.errorMessage = errorMsg
        this.userFound = false
      } finally {
        this.isLoading = false
      }
    },

    decodePassword(encoded, key = 'el_back_secret_key') {
      try {
        const decoded = atob(encoded)
        let password = ''
        for (let i = 0; i < decoded.length; i++) {
          password += String.fromCharCode(
            decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length)
          )
        }
        return password
      } catch (error) {
        console.error('Failed to decode password:', error)
        return '[Error decoding password]'
      }
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.decodedPassword).then(() => {
        this.copySuccess = true
        setTimeout(() => {
          this.copySuccess = false
        }, 2000)
      }).catch(() => {
        alert('Failed to copy to clipboard')
      })
    },
  },
}
</script>

<style scoped>
.users-modal-content {
  padding: 10px 0;
}

.user-found-section {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.password-display {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
}

.password-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.password-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.password-text {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 8px 12px;
  font-size: 0.95rem;
  font-family: 'Courier New', monospace;
  color: #0066cc;
  font-weight: 600;
  flex: 1;
  word-break: break-all;
}

.copy-success {
  font-size: 0.85rem;
  color: #28a745;
  margin-top: 8px;
  font-weight: 500;
}

.text-muted {
  color: #6c757d !important;
}

.user-slash {
  font-size: 3rem;
  color: #6c757d;
}
</style>
